import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { Sparkles, ShoppingBag, ArrowRight } from 'lucide-react';
import AnimatedBadge from './AnimatedBadge';
import { BADGE_TIERS } from '../lib/badges';

/** Deterministic star field (no RNG → SSR-safe, no hydration mismatch). */
const STARS = [
  { x: '8%', y: '18%', s: 2, d: 0 }, { x: '22%', y: '62%', s: 1.5, d: 1.2 },
  { x: '35%', y: '28%', s: 2.5, d: 0.6 }, { x: '48%', y: '72%', s: 1.5, d: 1.8 },
  { x: '61%', y: '22%', s: 2, d: 0.3 }, { x: '70%', y: '55%', s: 1.5, d: 1.5 },
  { x: '82%', y: '30%', s: 2.5, d: 0.9 }, { x: '90%', y: '66%', s: 2, d: 2.1 },
  { x: '15%', y: '42%', s: 1.5, d: 1.0 }, { x: '55%', y: '12%', s: 2, d: 0.4 },
  { x: '78%', y: '14%', s: 1.5, d: 1.7 }, { x: '40%', y: '50%', s: 2, d: 2.3 },
];

/**
 * "Your Rank Benefits" showcase — an always-dark hero banner for the Rewards
 * page. The five loyalty tiers climb a 3D staircase of glossy pedestals, each
 * badge rendered from the existing art via <AnimatedBadge>, over an animated
 * nebula (drifting glow blobs, light rays, twinkling stars). The viewer's
 * current rank (when signed in) is spotlighted with a beam + emerald ring.
 *
 * Self-contained and theme-independent: keeps its dark treatment in both light
 * and dark site themes so the screen-blended badge art always reads. All motion
 * is gated behind useReducedMotion().
 */
export default function RankBenefits({
  currentSlug = null,
  isLoggedIn = false,
}: {
  currentSlug?: string | null;
  isLoggedIn?: boolean;
}) {
  const reduce = useReducedMotion();

  return (
    <section
      className="relative overflow-hidden border-y border-emerald-500/20 px-6 py-10 sm:px-10 sm:py-14"
      style={{ background: 'linear-gradient(135deg, #072a1f 0%, #04120d 50%, #05202b 100%)' }}
    >
      {/* ── Ambient nebula layers ── */}
      {/* drifting glow blobs */}
      <motion.div
        className="absolute -top-24 -right-16 w-[28rem] h-[28rem] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.35), transparent 62%)', filter: 'blur(28px)' }}
        animate={reduce ? undefined : { x: [0, -24, 0], y: [0, 18, 0], scale: [1, 1.08, 1] }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute -bottom-28 -left-20 w-[26rem] h-[26rem] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle, rgba(45,212,191,0.22), transparent 62%)', filter: 'blur(32px)' }}
        animate={reduce ? undefined : { x: [0, 26, 0], y: [0, -16, 0], scale: [1, 1.12, 1] }}
        transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* light rays */}
      <motion.div
        className="absolute inset-0 pointer-events-none mix-blend-screen"
        style={{
          background:
            'conic-gradient(from 210deg at 80% -10%, transparent 0deg, rgba(110,231,183,0.14) 18deg, transparent 36deg, rgba(110,231,183,0.10) 54deg, transparent 72deg)',
        }}
        animate={reduce ? undefined : { opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      />
      {/* twinkling stars */}
      {STARS.map((st, i) => (
        <motion.span
          key={i}
          className="absolute rounded-full bg-white pointer-events-none"
          style={{ left: st.x, top: st.y, width: st.s, height: st.s }}
          animate={reduce ? undefined : { opacity: [0.15, 0.9, 0.15], scale: [0.8, 1.3, 0.8] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: st.d }}
        />
      ))}
      {/* noise grain */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.05]"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'n\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23n)\'/%3E%3C/svg%3E")',
        }}
      />

      <div className="relative max-w-6xl mx-auto flex flex-col lg:flex-row lg:items-center gap-8">
        {/* ── Left: copy + CTAs ── */}
        <div className="lg:w-[36%] shrink-0">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-emerald-300 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/10">
            <Sparkles size={13} /> ZS Rewards
          </span>
          <h2 className="mt-4 text-4xl sm:text-5xl font-extrabold leading-[1.05] text-white">
            Your Rank
            <br />
            <span
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(90deg, #34d399, #2dd4bf 60%, #4ade80)' }}
            >
              Benefits
            </span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-gray-300/90 max-w-sm">
            The more you shop, the higher you climb. Unlock exclusive rewards,
            higher cashback and premium perks at every rank.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/store"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-semibold text-sm transition-colors shadow-lg shadow-emerald-600/30"
            >
              <ShoppingBag size={16} /> Shop Now
            </Link>
            {isLoggedIn ? (
              <a
                href="#ranks"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/20 text-gray-100 hover:bg-white/10 font-semibold text-sm transition-colors"
              >
                View my rank <ArrowRight size={16} />
              </a>
            ) : (
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl border border-white/20 text-gray-100 hover:bg-white/10 font-semibold text-sm transition-colors"
              >
                Sign in <ArrowRight size={16} />
              </Link>
            )}
          </div>
        </div>

        {/* ── Right: 3D pedestal staircase ── */}
        <div className="relative flex-1 min-w-0">
          <div className="flex items-end justify-start lg:justify-center gap-2 sm:gap-4 overflow-x-auto lg:overflow-visible pb-1 px-1">
            {BADGE_TIERS.map((t, i) => {
              const pedH = 22 + i * 32; // rising steps — steep diagonal climb
              const size = 80 + i * 9; // badges grow with rank
              const pedW = size + 22;
              const range = t.max === Infinity ? `${t.min}+` : `${t.min}–${t.max}`;
              const isCurrent = t.slug === currentSlug;
              return (
                <motion.div
                  key={t.slug}
                  className="flex flex-col items-center shrink-0"
                  initial={reduce ? false : { opacity: 0, y: 24 }}
                  whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-40px' }}
                  transition={{ duration: 0.5, delay: 0.08 * i, ease: 'easeOut' }}
                >
                  {/* "You're here" chip */}
                  <span
                    className={`mb-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full transition-opacity ${isCurrent ? 'opacity-100' : 'opacity-0'}`}
                    style={{ background: 'rgba(16,185,129,0.18)', color: '#6ee7b7', border: '1px solid rgba(110,231,183,0.4)' }}
                  >
                    You’re here
                  </span>

                  {/* labels */}
                  <span className="text-sm sm:text-base font-bold text-white whitespace-nowrap">{t.name}</span>
                  <span className="text-[11px] sm:text-xs font-medium text-gray-400 whitespace-nowrap mb-2">{range}</span>

                  {/* badge + glow pool */}
                  <div className="relative" style={{ marginBottom: -size * 0.18 }}>
                    {/* subtle colored under-glow (kept small so the badge art colour dominates) */}
                    <div
                      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
                      style={{ width: size * 0.95, height: size * 0.95, background: `radial-gradient(circle, ${t.glow}44, transparent 70%)`, filter: 'blur(5px)', opacity: isCurrent ? 0.95 : 0.5 }}
                    />
                    <div
                      className="relative rounded-full"
                      style={isCurrent ? { boxShadow: `0 0 0 2px ${t.glow}, 0 0 24px -3px ${t.glow}`, borderRadius: '9999px' } : undefined}
                    >
                      <AnimatedBadge src={t.src} glow={t.glow} size={size} variant="full" alt={`${t.name} badge`} />
                    </div>
                  </div>

                  {/* 3D pedestal — bright lit stone block */}
                  <div className="relative" style={{ width: pedW }}>
                    {/* glowing top rim the coin rests on */}
                    <div
                      className="absolute left-1/2 -translate-x-1/2 -top-[6px] rounded-[50%]"
                      style={{
                        width: pedW * 0.9,
                        height: 13,
                        background: `radial-gradient(ellipse at center, ${t.glow} 0%, rgba(255,255,255,0.55) 35%, rgba(255,255,255,0.12) 70%, transparent 80%)`,
                        boxShadow: `0 0 16px -1px ${t.glow}`,
                        opacity: 0.9,
                      }}
                    />
                    {/* body — lighter slate with crisp top highlight + side shading */}
                    <div
                      className="rounded-t-xl border-x border-t"
                      style={{
                        height: pedH,
                        borderColor: 'rgba(255,255,255,0.22)',
                        background:
                          'linear-gradient(180deg, rgba(226,232,240,0.45) 0%, rgba(148,163,184,0.22) 16%, rgba(51,65,85,0.30) 60%, rgba(15,23,42,0.42) 100%), linear-gradient(90deg, rgba(0,0,0,0.22) 0%, rgba(255,255,255,0.14) 50%, rgba(0,0,0,0.26) 100%)',
                        boxShadow: 'inset 0 1.5px 0 rgba(255,255,255,0.4), 0 10px 20px -8px rgba(0,0,0,0.5)',
                      }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* subtle ground shadow under the staircase */}
          <div className="h-px w-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18) 20%, rgba(255,255,255,0.18) 80%, transparent)' }} />

          {/* sweeping line: a low swoosh from Aagman that hugs the bottom and
              rises to point at Asur (stays under the orbs, not through them) */}
          <svg
            aria-hidden
            className="pointer-events-none absolute inset-0 z-20 hidden h-full w-full overflow-visible lg:block"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            fill="none"
          >
            <motion.path
              d="M10 100 C 34 100, 58 98, 80 60"
              stroke="#34d399"
              strokeWidth={2.5}
              vectorEffect="non-scaling-stroke"
              strokeLinecap="round"
              fill="none"
              style={{ filter: 'drop-shadow(0 0 3px rgba(52,211,153,0.75))' }}
              initial={reduce ? false : { opacity: 0 }}
              whileInView={reduce ? undefined : { opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            />
          </svg>

          {/* arrowhead — tip pinned to the line's end (80%, 36%), undistorted */}
          <svg
            aria-hidden
            className="pointer-events-none absolute z-20 hidden overflow-visible lg:block"
            style={{ left: '80%', top: '60%', transform: 'translate(-26px, -9px)' }}
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
          >
            <motion.path
              d="M9 6 L26 9 L23 27"
              stroke="#34d399"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              style={{ filter: 'drop-shadow(0 0 3px rgba(52,211,153,0.75))' }}
              initial={reduce ? false : { opacity: 0 }}
              whileInView={reduce ? undefined : { opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: 1.2 }}
            />
          </svg>

          {/* tagline */}
          <p className="pointer-events-none absolute right-3 bottom-1 italic font-semibold text-emerald-300 text-sm leading-tight text-right drop-shadow-[0_1px_6px_rgba(16,185,129,0.5)]">
            Higher Rank
            <br />
            Bigger Rewards
          </p>
        </div>
      </div>
    </section>
  );
}
